import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, Truck, Package, Clock } from "lucide-react";

const OrderDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<any | null>(null);
  const [orderItems, setOrderItems] = useState<any[]>([]);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id) return;

      try {
        setLoading(true);

        // Fetch order details
        // Check if id looks like an order number (e.g., ORD-25-001303) or UUID
        const isOrderNumber = id.startsWith('ORD-');
        
        const query = supabase
          .from("orders")
          .select(`
            *,
            store_profiles:florist_id (
              store_name,
              phone,
              email
            )
          `);
        
        // Add appropriate filter based on ID type
        if (isOrderNumber) {
          query.eq("order_number", id);
        } else {
          query.eq("id", id);
        }
        
        // For authenticated users, filter by customer_id
        // For guest users coming from lookup, we've already verified email match
        if (user) {
          query.eq("customer_id", user.id);
        } else {
          // For guests, we rely on the fact that they had to provide correct email
          // to get to this page (enforced by GuestOrderLookup)
          // Just fetch the order by ID/order_number
        }
        
        const { data: orderData, error: orderError } = await query.single();

        if (orderError) throw orderError;
        setOrder(orderData);

        // Items are stored as JSON in the orders table, not a separate table
        const items = orderData.items || [];
        
        // Fetch product images for each item
        const itemsWithImages = await Promise.all(
          items.map(async (item: any) => {
            // First try to find the product by title
            const { data: productData } = await supabase
              .from('products')
              .select('id, main_image_url')
              .eq('title', item.name)
              .eq('store_id', orderData.florist_id)
              .single();
            
            if (!productData) {
              return { ...item, image_url: null };
            }
            
            // If we have a size, try to get size-specific image
            if (item.size && productData.id) {
              const { data: sizeData } = await supabase
                .from('product_sizes')
                .select('main_image_url')
                .eq('product_id', productData.id)
                .eq('size_name', item.size)
                .single();
              
              // Use size-specific image if available, otherwise fall back to product image
              return {
                ...item,
                image_url: sizeData?.main_image_url || productData.main_image_url || null
              };
            }
            
            return {
              ...item,
              image_url: productData.main_image_url || null
            };
          })
        );
        
        setOrderItems(itemsWithImages);
      } catch (error) {
        console.error("Error fetching order:", error);
        toast.error("Error loading order details");
        navigate(user ? "/customer-dashboard" : "/");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center py-12">
            <p>Loading order details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-background pt-24">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center py-12">
            <Card className="w-full max-w-3xl">
              <CardContent className="p-6">
                <div className="text-center py-8 space-y-4">
                  <h2 className="text-xl font-semibold">Order Not Found</h2>
                  <p>We couldn't find the order you're looking for.</p>
                  <Button onClick={() => navigate("/customer-dashboard")}>
                    Return to Dashboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <Package className="h-6 w-6 text-green-500" />;
      case "processing":
      case "ready":
        return <Clock className="h-6 w-6 text-amber-500" />;
      case "out_for_delivery":
        return <Truck className="h-6 w-6 text-blue-500" />;
      default:
        return <Clock className="h-6 w-6 text-gray-500" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-background pt-24">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            className="mb-6"
            onClick={() => navigate(user ? "/customer-dashboard" : "/guest-order-lookup")}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            {user ? "Back to Dashboard" : "Back to Order Lookup"}
          </Button>

          <div className="grid gap-6">
            {/* Order Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Order {order.order_number || `#${order.id.slice(0, 8)}`}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-medium text-sm text-muted-foreground mb-2">Order Information</h3>
                    <div className="space-y-1">
                      <p><span className="font-medium">Date: </span>{formatDate(order.created_at)}</p>
                      <p><span className="font-medium">Total: </span>${order.total_amount}</p>
                      <p><span className="font-medium">Payment Method: </span>{order.payment_method || "Credit Card"}</p>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-medium text-sm text-muted-foreground mb-2">Shop Information</h3>
                    <div className="space-y-1">
                      <p><span className="font-medium">Shop: </span>{order.store_profiles?.store_name}</p>
                      <p><span className="font-medium">Contact: </span>{order.store_profiles?.phone}</p>
                      <p><span className="font-medium">Email: </span>{order.store_profiles?.email}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Order Status */}
            <Card>
              <CardHeader>
                <CardTitle>Order Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-4">
                  {getStatusIcon(order.status)}
                  <div>
                    <div className="font-medium capitalize">{order.status.replace("_", " ")}</div>
                    <p className="text-sm text-muted-foreground">
                      {order.status === "completed"
                        ? "Your order has been delivered."
                        : order.status === "out_for_delivery"
                        ? "Your order is on its way to you!"
                        : order.status === "ready"
                        ? "Your order is ready for pickup/delivery."
                        : order.status === "processing"
                        ? "Your order is being prepared."
                        : order.status === "pending"
                        ? "Your order has been received."
                        : "Status is being updated."}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Order Items */}
            <Card>
              <CardHeader>
                <CardTitle>Order Items</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {orderItems.length === 0 ? (
                    <p className="text-muted-foreground">No items found for this order.</p>
                  ) : (
                    orderItems.map((item, index) => (
                      <div key={`${item.name}-${item.size}-${index}`} className="flex items-start border-b pb-4 last:border-0 last:pb-0">
                        <div className="w-16 h-16 rounded-md overflow-hidden bg-brand-accent mr-4 flex-shrink-0">
                          {item.image_url ? (
                            <img
                              src={item.image_url}
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-[#967F81]/10">
                              <Package className="h-6 w-6 text-brand-primary/50" />
                            </div>
                          )}
                        </div>
                        <div className="flex-grow">
                          <p className="font-medium">{item.name || "Product"}</p>
                          {item.size && <p className="text-sm text-muted-foreground">Size: {item.size}</p>}
                          <div className="flex justify-between mt-1">
                            <p className="text-sm text-muted-foreground">Qty: {item.quantity || 1}</p>
                            <p className="font-medium">${item.price || 0}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Delivery Information */}
            {order.delivery_address && (
              <Card>
                <CardHeader>
                  <CardTitle>Delivery Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div>
                      <span className="font-medium">Address: </span>
                      {(() => {
                        try {
                          const addr = typeof order.delivery_address === 'string' 
                            ? JSON.parse(order.delivery_address) 
                            : order.delivery_address;
                          return `${addr.street}, ${addr.suburb}, ${addr.state} ${addr.postcode}`;
                        } catch (e) {
                          return order.delivery_address;
                        }
                      })()}
                    </div>
                    {order.special_instructions && (
                      <p><span className="font-medium">Instructions: </span>{order.special_instructions}</p>
                    )}
                    {order.delivery_date && (
                      <p><span className="font-medium">Scheduled for: </span>{formatDate(order.delivery_date)}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails; 